import { useEffect, useImperativeHandle, useState } from "react";
import type { SearchHookSuggestion, MapSearchProps } from "../../../types";
import { useMapSearch } from "../hooks/useMapSearch";
import { useDebounce } from "use-debounce";
import { config } from "@config";

export function SearchBox({
  token,
  proximity,
  types,
  countries = config.supportedCountries,
  debounce = 300,
  hideResultsTimeout = 300,
  searchBoxRef,
  renderInput,
  renderSuggestion,
  renderEmpty,
  renderCurrentLocationButton,
  containerProps,
  resultsContainerProps,
  onSearched,
  onRetrieved,
  onSearchError,
  onRetrieveError,
  onCurrentLocationError,
}: MapSearchProps) {
  const [inputValue, setInputValue] = useState("");
  const [debouncedInputValue] = useDebounce(inputValue, debounce);

  const [searchResultsVisible, setSearchResultsVisible] = useState(false);
  const [requestInProgress, setRequestInProgress] = useState(false);

  const [searchResults, setSearchResults] = useState<SearchHookSuggestion[]>(
    [],
  );

  const { suggest, retrieve, fromCoords } = useMapSearch({
    token,
    types,
    proximity,
  });

  async function getSuggestions(query: string) {
    if (requestInProgress) {
      return;
    }

    setRequestInProgress(true);

    try {
      const result = await suggest(query, {
        countries: [...countries],
      });
      setSearchResults(result.suggestions);
      onSearched?.(result);
    } catch (err) {
      onSearchError?.(err as Error);
    } finally {
      setRequestInProgress(false);
    }
  }

  async function retrieveSuggestion(suggestion: SearchHookSuggestion) {
    if (requestInProgress) {
      return;
    }

    setRequestInProgress(true);

    try {
      const result = await retrieve(suggestion);
      onRetrieved?.(result);
    } catch (err) {
      onRetrieveError?.(err as Error);
    } finally {
      setRequestInProgress(false);
    }
  }

  useImperativeHandle(searchBoxRef, () => ({
    selectFirstSuggestion: async () => {
      if (searchResults.length > 0) {
        await retrieveSuggestion(searchResults[0]);
      }
    },
  }), [searchResults]);

  async function useCurrentLocation() {
    if (!navigator.geolocation) {
      onCurrentLocationError?.(new Error("Geolocation is not supported"));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const result = await fromCoords({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });

        if (result.locations.length > 0) {
          const firstLocation = result.locations[0];
          setInputValue(firstLocation.name);
          onRetrieved?.(result);
        }
      },
      (error) => {
        onCurrentLocationError?.(error);
      },
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 300_000 },
    );
  }

  function updateSearchValue(value: string) {
    setInputValue(value);
  }

  function displayResults() {
    setSearchResultsVisible(true);
  }

  function hideResults() {
    setTimeout(() => {
      setSearchResultsVisible(false);
    }, hideResultsTimeout);
  }

  useEffect(() => {
    const value = debouncedInputValue.trim();
    if (value.length === 0) {
      return;
    }

    setSearchResultsVisible(true);
    getSuggestions(value);
  }, [debouncedInputValue]);

  useEffect(() => {
    setSearchResultsVisible(searchResults.length > 0);
  }, [searchResults]);

  return (
    <div {...containerProps}>
      {renderInput({ updateSearchValue, displayResults, hideResults })}
      {searchResultsVisible && (
        <div {...resultsContainerProps}>
          {debouncedInputValue.length === 0 &&
            renderEmpty?.({
              currentLocationButton: renderCurrentLocationButton?.({
                useCurrentLocation,
              }),
            })}

          {searchResults.map((suggestion, idx) => (
            <div key={idx}>
              {renderSuggestion({
                suggestion,
                onSelect: () => retrieveSuggestion(suggestion),
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

SearchBox.displayName = "SearchBox";
